import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { MarketplacePaymentRevision } from '../data-models/marketplace-payment-revision.model';
import { MarketplacePayment } from '../data-models/marketplace-payment.model';
import { AdjustPaymentRequest } from '../request-models/adjust-payment-request.model';
import { AllPaymentsFilters } from '../request-models/all-payments-filters.model';
import { ApprovePaymentRequest } from '../request-models/approve-payment-request.model';
import { DeclinePaymentRequest } from '../request-models/decline-payment-request.model';
import { PendingApprovalFilters } from '../request-models/pending-approval-filters.model';
import { PaymentActionOutcome } from '../response-models/payment-action-outcome.model';
import { isRecord } from '../utils/is-record';
import {
  toAdjustPaymentRequest,
  toAllPaymentsQuery,
  toApprovePaymentRequest,
  toDeclinePaymentRequest,
  toEarlyReleaseRequest,
  toPendingApprovalQuery,
} from '../utils/marketplace-payment-request-utils';
import { nullableString } from '../utils/wire-value-utils';

/**
 * Marketplace payments: `/admin/payments` (hub `admin-payments-controller.ts`). The lists are paged with
 * `limit` / `offset` and return a plain array (no paging block). Also the deferred-capture approval actions,
 * the early payout release and the revision timeline. Disputes have their own `DisputeService`.
 *
 * Action failures (`in_flight`, `failed_retryable`, `failed_permanent`, …) arrive as an `ApiError` whose `code` is
 * the outcome upper-cased and whose `detail` may carry `declineCode`.
 */
@Injectable({ providedIn: 'root' })
export class MarketplacePaymentService extends BaseService<MarketplacePayment> {
  constructor() {
    super('admin/payments', MarketplacePayment);
  }

  /** GET /admin/payments/pending-approval: rows awaiting an admin capture decision. */
  listPendingApproval(filters: PendingApprovalFilters = {}): Observable<MarketplacePayment[]> {
    return this.getMany({ path: 'pending-approval', query: toPendingApprovalQuery(filters) });
  }

  /** GET /admin/payments: every lifecycle status (or only `filters.statuses`). */
  listAllPayments(filters: AllPaymentsFilters = {}): Observable<MarketplacePayment[]> {
    return this.getMany({ query: toAllPaymentsQuery(filters) });
  }

  /** GET /admin/payments/:id. Emits `null` when the hub returns no data. */
  getPayment(paymentId: string): Observable<MarketplacePayment | null> {
    return this.get(paymentId);
  }

  /** GET /admin/payments/:id/revisions, oldest first. */
  getRevisions(paymentId: string): Observable<MarketplacePaymentRevision[]> {
    return this.request('GET', { id: paymentId, path: 'revisions' }, toRevisions);
  }

  /** POST /admin/payments/pending-approval/:id/approve: capture, with an optional inline adjustment. */
  approvePayment(paymentId: string, body: ApprovePaymentRequest = {}): Observable<PaymentActionOutcome> {
    return this.pendingApprovalAction(paymentId, 'approve', toApprovePaymentRequest(body));
  }

  /** POST /admin/payments/pending-approval/:id/adjust: adjust without capturing. */
  adjustPayment(paymentId: string, body: AdjustPaymentRequest): Observable<PaymentActionOutcome> {
    return this.pendingApprovalAction(paymentId, 'adjust', toAdjustPaymentRequest(body));
  }

  /** POST /admin/payments/pending-approval/:id/decline */
  declinePayment(paymentId: string, body: DeclinePaymentRequest): Observable<PaymentActionOutcome> {
    return this.pendingApprovalAction(paymentId, 'decline', toDeclinePaymentRequest(body));
  }

  /** POST /admin/payments/payouts/:id/release with `ignoreWindow` and the audited reason. */
  releasePayoutEarly(paymentId: string, reason: string): Observable<PaymentActionOutcome> {
    return this.request('POST', { path: ['payouts', paymentId, 'release'] }, toOutcome, toEarlyReleaseRequest(reason));
  }

  private pendingApprovalAction(paymentId: string, action: string, body: unknown): Observable<PaymentActionOutcome> {
    return this.request('POST', { path: ['pending-approval', paymentId, action] }, toOutcome, body);
  }
}

function toRevisions(data: unknown): MarketplacePaymentRevision[] {
  return Array.isArray(data) ? data.map(item => new MarketplacePaymentRevision(item)) : [];
}

/** Copies only the outcome fields the page reads; `outcome` is `''` when the hub sent none. */
export function toOutcome(data: unknown): PaymentActionOutcome {
  const source = isRecord(data) ? data : {};
  return {
    outcome: nullableString(source['outcome']) ?? '',
    paymentId: nullableString(source['paymentId']) ?? undefined,
    reason: nullableString(source['reason']) ?? undefined,
    declineCode: nullableString(source['declineCode']),
  };
}
